/**
 * CreatorPortal - Admin dashboard for content creation tools
 *
 * Features:
 * - Admin role guard (redirects non-admins to home)
 * - Tool cards grid for accessing content creation features
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import AppLayout from "@/components/layout/AppLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
    BookOpen,
    Sparkles,
    ArrowRight,
    Users,
    Megaphone,
} from "lucide-react";

/**
 * Tool card data structure
 */
interface ToolCard {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    iconBgColor: string;
    comingSoon?: boolean;
    onClick?: () => void;
}

const CreatorPortal = () => {
    const navigate = useNavigate();
    useAuth(); // Auth context needed for hooks
    const { isAdmin, isLoading: isAdminLoading, error: adminError } = useAdminRole();

    // Redirect non-admins to home
    useEffect(() => {
        if (!isAdminLoading && !isAdmin) {
            navigate("/", { replace: true });
        }
    }, [isAdmin, isAdminLoading, navigate]);

    // Loading state
    if (isAdminLoading) {
        return (
            <AppLayout pageTitle="Creator Portal">
                <div className="min-h-screen bg-background">
                    <div className="max-w-6xl mx-auto px-6 py-12">
                        <Skeleton className="h-10 w-64 mb-2" />
                        <Skeleton className="h-5 w-96 mb-12" />
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3].map((i) => (
                                <Skeleton key={i} className="h-64 rounded-2xl" />
                            ))}
                        </div>
                    </div>
                </div>
            </AppLayout>
        );
    }

    // Error or not admin (will redirect, but show nothing in the meantime)
    if (adminError || !isAdmin) {
        return (
            <AppLayout pageTitle="Creator Portal">
                <div className="min-h-screen bg-background flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Redirecting...</p>
                    </div>
                </div>
            </AppLayout>
        );
    }

    // Tool cards configuration
    const tools: ToolCard[] = [
        {
            id: "journal-generator",
            title: "Journal Generator",
            description:
                "Create comprehensive learning journals with AI-generated modules, quizzes, videos, and todo items from a single prompt.",
            icon: <BookOpen className="w-6 h-6" />,
            iconBgColor: "bg-violet-100 text-violet-600",
            onClick: () => navigate('/admin/generator'),
        },
        {
            id: "user-management",
            title: "User Management",
            description:
                "View and manage users, their subscription status, and credits. Search users and grant credits manually.",
            icon: <Users className="w-6 h-6" />,
            iconBgColor: "bg-blue-100 text-blue-600",
            onClick: () => navigate('/admin/users'),
        },
        {
            id: "announcements",
            title: "Announcements",
            description:
                "Create and manage in-app announcements, event notifications, and tips. Optionally send email blasts.",
            icon: <Megaphone className="w-6 h-6" />,
            iconBgColor: "bg-amber-100 text-amber-600",
            onClick: () => navigate('/admin/announcements'),
        },
    ];

    return (
        <AppLayout pageTitle="Creator Portal">
            <div className="min-h-screen bg-background">
                <div className="max-w-6xl mx-auto px-6 py-12">
                    {/* Header */}
                    <div className="mb-12">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-foreground rounded-xl">
                                <Sparkles className="w-5 h-5 text-background" />
                            </div>
                            <h1 className="text-3xl font-bold text-foreground tracking-tight">
                                Creator Portal
                            </h1>
                        </div>
                        <p className="text-muted-foreground text-lg max-w-2xl">
                            Powerful tools for creating and managing learning content at scale.
                        </p>
                    </div>

                    {/* Tool Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {tools.map((tool) => (
                            <div
                                key={tool.id}
                                className={`
                                    group relative bg-card border border-border rounded-2xl p-6 
                                    transition-all duration-300 hover:shadow-xl hover:shadow-muted/50 
                                    hover:border-muted-foreground/30 hover:-translate-y-1
                                    ${tool.comingSoon ? "opacity-75" : "cursor-pointer"}
                                `}
                                onClick={tool.comingSoon ? undefined : tool.onClick}
                            >
                                {/* Coming Soon Badge */}
                                {tool.comingSoon && (
                                    <div className="absolute top-4 right-4">
                                        <span className="px-2.5 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full">
                                            Coming Soon
                                        </span>
                                    </div>
                                )}

                                {/* Icon */}
                                <div
                                    className={`
                                        w-12 h-12 rounded-xl flex items-center justify-center mb-4
                                        ${tool.iconBgColor}
                                        transition-transform duration-300 group-hover:scale-110
                                    `}
                                >
                                    {tool.icon}
                                </div>

                                {/* Title */}
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {tool.title}
                                </h3>

                                {/* Description */}
                                <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                                    {tool.description}
                                </p>

                                {/* Action Button */}
                                {!tool.comingSoon && (
                                    <Button
                                        variant="ghost"
                                        className="absolute bottom-4 right-4 p-2 h-auto rounded-full 
                                            opacity-0 group-hover:opacity-100 transition-opacity
                                            bg-muted hover:bg-muted/80"
                                    >
                                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default CreatorPortal;
